import PageContainer from "../components/PageContainer";
import photo1 from '../static/images/partner/1.jpg';
import photo2 from '../static/images/partner/2.jpg';
import photo3 from '../static/images/partner/3.jpg';
import photo4 from '../static/images/partner/4.jpg';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setBacklink } from "../redux/backlinkSlice";
import { Helmet } from 'react-helmet-async';
import usePageTracking from "../hooks/usePageTracking";
import { useTranslation } from "react-i18next";

// Vantaggi per i partner: la chiave i18n vale sia per il titolo (`key`) sia per il testo (`keyDescription`)
const ADVANTAGES = [
    { key: 'greaterVisibility', icon: 'fi fi-rr-chart-line-up' },
    { key: 'lessAdministrativeWork', icon: 'fi fi-rr-clock' },
    { key: 'simpleReservation', icon: 'fi fi-rr-calendar-check' },
    { key: 'maximumFlexibility', icon: 'fi fi-rr-settings-sliders' },
    { key: 'noSubscriptionCost', icon: 'fi fi-rr-piggy-bank' },
    { key: 'securePayments', icon: 'fi fi-rr-credit-card' },
    { key: 'collaborationNetwork', icon: 'fi fi-rr-users-alt' },
];

const PartnerPage = () => {

    const { pageTitle } = usePageTracking({
        pageTitle: "Partner",
    })

    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handleClick = () => {
        dispatch(setBacklink("/partner-page"));
        navigate("/partner-terms-and-conditions");
    }

    return (
        <PageContainer items="center" px="4">
            <Helmet>
                <title>{pageTitle} - TicinoWild</title>
                <meta name="description" content="Diventa partner di TicinoWild oggi stesso ed espandi la tua visibilità nel mercato delle attività outdoor" />
                <link rel="canonical" href="https://ticinowild.ch/partner" />
                <meta property="og:title" content={`${pageTitle} - TicinoWild`} />
                <meta property="og:description" content="Diventa partner di TicinoWild oggi stesso ed espandi la tua visibilità nel mercato delle attività outdoor" />
                <meta property="og:url" content="https://ticinowild.ch/partner" />
                <meta name="twitter:title" content={`${pageTitle} - TicinoWild`} />
                <meta name="twitter:description" content="Diventa partner di TicinoWild oggi stesso ed espandi la tua visibilità nel mercato delle attività outdoor" />
            </Helmet>
            <div className="max-w-[560px]">
                <h3 className="text-[#225424] text-center">{t('PartnerPage.title')}</h3>
                {/* Più respiro fra titolo e sottotitolo (revisione cliente 2026-08) */}
                <div className="text-xl font-bold text-center mt-6">{t("PartnerPage.becomePartnerOf")} <span className="font-signature text-2xl text-[#b05704]">TicinoWild</span> {t("PartnerPage.today")}</div>
            </div>
            <div className="flex flex-col md:flex-row gap-2 mt-8">
                <div className="flex flex-row gap-2 justify-center">
                    <div>
                        <div className="w-[160px] h-[160px] overflow-hidden"><img src={photo1} alt="foto partner 1" className="h-full w-full object-cover rounded-xl" /></div>
                        <div className="h-[50px]"></div>
                    </div>
                    <div>
                        <div className="h-[50px]"></div>
                        <div className="w-[160px] h-[160px] overflow-hidden"><img src={photo2} alt="foto partner 2" className="h-full w-full object-cover rounded-xl" /></div>
                    </div>
                </div>
                <div className="flex flex-row gap-2 justify-center mt-[-50px] md:mt-0">
                    <div>
                        <div className="w-[160px] h-[160px] overflow-hidden"><img src={photo3} alt="foto partner 3" className="h-full w-full object-cover rounded-xl" /></div>
                        <div className="h-[50px]"></div>
                    </div>
                    <div>
                        <div className="h-[50px]"></div>
                        <div className="w-[160px] h-[160px] overflow-hidden"><img src={photo4} alt="foto partner 4" className="h-full w-full object-cover rounded-xl" /></div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-8 max-w-[600px] mt-8">
                <h4 className="text-center">{t("PartnerPage.advantages")}</h4>
                {
                    ADVANTAGES.map(({ key, icon }) => (
                        <div key={key} className="flex gap-4">
                            <i className={`${icon} text-2xl text-[#225424] leading-none mt-1 shrink-0`}></i>
                            <div>
                                <h4 className="text-[#225424]">{t(`PartnerPage.${key}`)}</h4>
                                <p>
                                    {t(`PartnerPage.${key}Description`)}
                                </p>
                            </div>
                        </div>
                    ))
                }
                <p className="underline cursor-pointer text-[#225424] w-fit" onClick={handleClick}>
                    {t("PartnerPage.termsAndConditions")}
                </p>
            </div>

            {/* Box "Programma Partner Premium" (aggiunta richiesta dal cliente 2026-07-28) */}
            <div className="w-full max-w-[860px] mt-12 border border-[#225424]/40 bg-[#eef7ef] rounded-2xl px-4 py-6 md:px-8 md:py-8">
                {/* Badge: pastiglia verde con medaglia sporgente a sinistra */}
                <div className="flex items-center">
                    <div className="w-11 h-11 rounded-full bg-[#225424] flex items-center justify-center shrink-0 z-10">
                        <i className="fi fi-rr-badget-check-alt text-white text-2xl leading-none mt-1"></i>
                    </div>
                    <div className="bg-[#225424] text-white text-sm font-bold rounded-lg py-1.5 pl-6 pr-4 ml-[-14px]">
                        {t("PartnerPage.premium.badge")}
                    </div>
                </div>

                <h4 className="text-[#225424] text-lg md:text-xl text-center mt-5">{t("PartnerPage.premium.title")}</h4>

                <p className="mt-6">{t("PartnerPage.premium.intro")}</p>

                <h4 className="text-[#3d7a3f] mt-6">{t("PartnerPage.premium.planTitle")}</h4>
                <p className="mt-2">{t("PartnerPage.premium.planIntro")}</p>

                <ul className="mt-5 grid md:grid-cols-2 gap-x-8 gap-y-3 marker:text-[#225424]">
                    <li>{t("PartnerPage.premium.item1")}</li>
                    <li>{t("PartnerPage.premium.item2")}</li>
                    <li>{t("PartnerPage.premium.item3")}</li>
                    <li>{t("PartnerPage.premium.item4")}</li>
                    <li>{t("PartnerPage.premium.item5")}</li>
                    <li>{t("PartnerPage.premium.item6")}</li>
                </ul>

                <p className="mt-6">{t("PartnerPage.premium.closing")}</p>
            </div>

            <div className="mt-12 text-center max-w-[600px]">
                <h4 className="text-[#225424]">{t("PartnerPage.ctaTitle")}</h4>
                <p className="mt-1">{t("PartnerPage.ctaSubtitle")}</p>
            </div>
            <div className="mt-6">
                <Link to="https://forms.gle/iaEmYH2kNB7M2Gg59" target="_blank"><button className='bg-[#fbd100] text-white font-bold py-4 px-8 md:py-2 text-xl md:text-sm rounded-xl'>{t("PartnerPage.startNowButton")}</button></Link>
            </div>
        </PageContainer>
    )
}

export default PartnerPage